import React, { useState } from "react";
import PropTypes from "prop-types";
import { UI_CONSTANTS } from "../utils/constants";
import { isValidHexColor } from "../utils/formatters";
import "./ColorPicker.css";

const PRESET_COLORS = [
  "#007bff", // Blue
  "#28a745", // Green
  "#dc3545", // Red
  "#ffc107", // Yellow
  "#17a2b8", // Cyan
  "#6f42c1", // Purple
  "#fd7e14", // Orange
  "#20c997", // Teal
  "#e83e8c", // Pink
  "#6c757d", // Gray
  "#343a40", // Dark
  "#f8f9fa", // Light
];

const ColorPicker = ({
  value = "#007bff",
  onChange = null,
  label = "",
  showPresets = true,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleColorChange = (newColor) => {
    if (disabled || !onChange) return;
    onChange(newColor);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    if (disabled) return;

    let input = e.target.value;

    // Auto-add # if not present
    if (input && !input.startsWith("#")) {
      input = "#" + input;
    }

    if (onChange) {
      onChange(input);
    }
  };

  // Only show invalid state when the value is a "complete" length (#RGB = 4 chars
  // or #RRGGBB = 7 chars) but fails validation. Partial input (e.g., "#0") is not
  // flagged — the user is still typing.
  const isComplete = value && (value.length === 4 || value.length >= 7);
  const showInvalid = isComplete && !isValidHexColor(value);

  return (
    <div className="color-picker">
      {label && <label className="color-picker-label">{label}</label>}

      <div className="color-picker-input-group">
        <button
          type="button"
          className="color-picker-swatch"
          style={{ backgroundColor: isValidHexColor(value) ? value : "#ccc" }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          title={
            disabled
              ? "Color customization disabled on your plan"
              : "Click to pick color"
          }
          aria-label="Toggle color presets"
        />

        <input
          type="text"
          className={`color-picker-input ${showInvalid ? "invalid" : ""}`}
          value={value}
          onChange={handleInputChange}
          placeholder="#007bff"
          maxLength={UI_CONSTANTS.HEX_COLOR_MAX_LENGTH}
          disabled={disabled}
          aria-label={label || "Color hex code"}
        />
        {showInvalid && (
          <span className="color-picker-error">Invalid hex color</span>
        )}

        <input
          type="color"
          className="color-picker-native"
          value={isValidHexColor(value) ? value : "#007bff"}
          onChange={(e) => handleColorChange(e.target.value)}
          disabled={disabled}
          title={
            disabled
              ? "Color customization disabled on your plan"
              : "Use color picker"
          }
          aria-label="Choose color from picker"
        />
      </div>

      {showPresets && isOpen && !disabled && (
        <div className="color-picker-presets" aria-label="Preset colors">
          <div className="color-picker-presets-header">Preset Colors</div>
          <div className="color-picker-presets-grid">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-picker-preset ${value === color ? "selected" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorChange(color)}
                title={color}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

ColorPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  showPresets: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ColorPicker;
